import React, { useCallback, useEffect, useState } from "react";
import { devicesAPI, deviceToken } from "../services/api";
import { useI18n } from "../i18n";
import { formatDate } from "../utils/dates";

/**
 * The shop's licensed devices.
 *
 * Releasing a slot is self-service so a broken tablet can be replaced without
 * a phone call; raising the limit is not, because that is what the
 * subscription sells.
 */
const Devices = () => {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [releasing, setReleasing] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const lastUsed = (value) => {
    if (!value) return t("devices.never");

    const days = Math.floor((Date.now() - new Date(value)) / 86400000);

    if (days <= 0) return t("common.today");
    if (days === 1) return t("common.yesterday");
    if (days < 30) return t("devices.daysAgo", { count: days });

    return formatDate(value);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await devicesAPI.list();
      setState(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("devices.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const release = async (device) => {
    setReleasing(device.id);

    try {
      await devicesAPI.revoke(device.id);

      // Releasing the device you are standing at logs this browser out of the
      // shop, so clear the token rather than leaving it to fail on next call.
      if (device.id === state.current_device_id) {
        deviceToken.clear();
        window.location.reload();
        return;
      }

      setConfirming(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t("devices.releaseFailed"));
    } finally {
      setReleasing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
          {t("devices.title")}
        </h1>
        <p className="text-gray-600">{t("devices.subtitle")}</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {state && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
            <p className="text-gray-800 font-semibold">
              {t("devices.inUse", {
                used: state.slots_used,
                limit: state.device_limit,
              })}
            </p>
            {state.slots_free === 0 ? (
              <p className="text-sm text-amber-700">{t("devices.allTaken")}</p>
            ) : (
              <p className="text-sm text-gray-500">
                {t("devices.available", { count: state.slots_free })}
              </p>
            )}
          </div>

          <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden mb-6">
            <div
              className={`h-full rounded-full ${
                state.slots_free === 0 ? "bg-amber-500" : "bg-primary-600"
              }`}
              style={{
                width: `${Math.min(
                  (state.slots_used / state.device_limit) * 100,
                  100,
                )}%`,
              }}
            />
          </div>

          <ul className="divide-y divide-gray-100">
            {state.devices.map((device) => {
              const isCurrent = device.id === state.current_device_id;

              return (
                <li
                  key={device.id}
                  className="py-4 flex flex-wrap items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 flex flex-wrap items-center gap-2">
                      {device.label}
                      {isCurrent && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                          {t("devices.thisDevice")}
                        </span>
                      )}
                      {device.dormant && !isCurrent && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {t("devices.unused")}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t("devices.lastUsed", { when: lastUsed(device.last_seen) })}
                    </p>
                  </div>

                  {confirming === device.id ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {isCurrent
                          ? t("devices.signsOut")
                          : t("devices.releaseIt")}
                      </span>
                      <button
                        onClick={() => release(device)}
                        disabled={releasing === device.id}
                        className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-60"
                      >
                        {releasing === device.id
                          ? t("devices.releasing")
                          : t("devices.release")}
                      </button>
                      <button
                        onClick={() => setConfirming(null)}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                      >
                        {t("common.keep")}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirming(device.id)}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                    >
                      {t("devices.release")}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>

          <p className="mt-6 text-sm text-gray-500 border-t border-gray-100 pt-4">
            {t("devices.moreDevices", { limit: state.device_limit })}
          </p>
        </div>
      )}
    </div>
  );
};

export default Devices;
